/**
 * Admin → Visit Types: every task type on one grid, with the clinic picked
 * above it (or all clinics) for Create on admission.
 */

import React, { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import { useStore } from '../../../store';
import { PermissionSlug } from '../../../constants/featureFlags';
import { ALL_CLINICS } from './clinicScope';
import { useVisitTypesData, type VisitTypesData } from './useVisitTypesData';
import { ALL_KINDS } from './kinds';
import { VisitTypesGrid } from './VisitTypesGrid';

/** Past this many changes, Save asks first. */
const CONFIRM_OVER = 10;

const SaveControls: React.FC<{ data: VisitTypesData }> = ({ data }) => {
  const [confirming, setConfirming] = useState(false);
  const count = data.pendingCount;
  const save = () => {
    setConfirming(false);
    void data.save();
  };
  return (
    <div className='flex items-center gap-2'>
      <span className='text-xs text-muted-foreground'>
        {count === 0
          ? 'No unsaved changes'
          : `${count} unsaved ${count === 1 ? 'change' : 'changes'}`}
      </span>
      <Button
        size='sm'
        variant='outline'
        disabled={count === 0 || data.saving}
        onClick={data.revert}
      >
        <RotateCcw className='mr-1.5 h-3.5 w-3.5' />
        Revert
      </Button>
      <Button
        size='sm'
        disabled={count === 0 || data.saving}
        onClick={() => (count > CONFIRM_OVER ? setConfirming(true) : save())}
      >
        {data.saving && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
        Save
      </Button>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Save {count} changes?</DialogTitle>
            <DialogDescription>
              These apply to every visit scheduled from now on, and Revert
              can&apos;t undo them once saved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button onClick={save}>Save {count} changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const VisitTypesSection: React.FC = () => {
  const { hasAnyPermission } = useStore(
    useShallow(state => ({ hasAnyPermission: state.hasAnyPermission }))
  );
  const canReadVisitTypes = hasAnyPermission([
    PermissionSlug.TASK_TYPES_VIEW,
    PermissionSlug.TASK_TYPES_EDIT,
  ]);
  const canEditVisitTypes = hasAnyPermission([PermissionSlug.TASK_TYPES_EDIT]);
  const canReadAutoCreate = hasAnyPermission([
    PermissionSlug.PATIENT_CREATION_SETTINGS_VIEW,
    PermissionSlug.PATIENT_CREATION_SETTINGS_EDIT,
  ]);
  const canEditAutoCreate = hasAnyPermission([
    PermissionSlug.PATIENT_CREATION_SETTINGS_EDIT,
  ]);

  const [scope, setScope] = useState(ALL_CLINICS);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState(ALL_KINDS);
  const data = useVisitTypesData({
    scope,
    canReadVisitTypes,
    canReadAutoCreate,
  });

  // A column the user cannot read is left out, not shown empty.
  const hiddenColumnIds = [
    ...(canReadVisitTypes ? [] : ['aliases', 'billable']),
    ...(canReadAutoCreate ? [] : ['auto_create']),
  ];

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-3'>
          <div className='relative w-full sm:w-64'>
            <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
            <Input
              inputSize='sm'
              placeholder='Search task types'
              aria-label='Search task types'
              value={search}
              onChange={e => setSearch(e.target.value)}
              className='w-full pl-8'
            />
          </div>
          {canReadAutoCreate && (
            <div className='w-full sm:w-64'>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger aria-label='Clinic' selectSize='sm'>
                  <SelectValue placeholder='Select a clinic' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_CLINICS}>All clinics</SelectItem>
                  <SelectSeparator />
                  {data.clinics.map(clinic => (
                    <SelectItem key={clinic.id} value={clinic.id}>
                      {clinic.name || 'Unnamed clinic'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {(canEditVisitTypes || canEditAutoCreate) && (
          <SaveControls data={data} />
        )}
      </div>

      {data.loadFailed ? (
        <div className='rounded-md border py-8 text-center text-sm text-muted-foreground'>
          Couldn&apos;t load visit types. Please try again.
        </div>
      ) : data.loading ? (
        <div className='flex items-center justify-center py-16'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
        </div>
      ) : (
        <VisitTypesGrid
          data={data}
          search={search}
          kindFilter={kindFilter}
          onKindFilterChange={setKindFilter}
          canEditVisitTypes={canEditVisitTypes}
          canEditAutoCreate={canEditAutoCreate}
          hiddenColumnIds={hiddenColumnIds}
        />
      )}
    </div>
  );
};

export default VisitTypesSection;
